import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

interface EmptyStateProps {
  /** 좌상단에 놓일 아이콘 (Lucide) — 없으면 아이콘 영역 생략 */
  icon?: LucideIcon;
  /** 주 제목 (굵게, 대비 강조) */
  title: string;
  /** 부제 (회색 톤, 선택) */
  description?: string;
  /** 하단 액션 (버튼 등) — Button/Link 컴포넌트 직접 전달 */
  action?: React.ReactNode;
  /** 컨테이너 여백 조정용 */
  className?: string;
  /** 기본 24px 원형 아이콘 영역 대신 다른 크기가 필요할 때 */
  size?: 'sm' | 'md';
}

/**
 * Empty state — 리스트/피드 비었을 때 노출하는 공통 컴포넌트.
 *
 * 디자인 원칙 (토스증권 기준):
 *  - 아이콘은 원형 배경 + 중간 채도 아이콘, 튀지 않게
 *  - 제목: 1줄, font-semibold
 *  - 부제: 1~2줄, text-sub
 *  - 점선 테두리 (dashed) 로 "비어있음" 상태 시각화
 *  - 대비 높은 컬러 남발 금지 — 조용한 상태 표현
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  size = 'md',
}: EmptyStateProps) {
  const iconBoxCls = size === 'sm' ? 'h-12 w-12' : 'h-14 w-14';
  const iconCls = size === 'sm' ? 'h-6 w-6' : 'h-7 w-7';
  const padCls = size === 'sm' ? 'py-12' : 'py-20';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--color-border)] text-center',
        padCls,
        className,
      )}
    >
      {Icon && (
        <div
          className={cn(
            'mb-3 flex items-center justify-center rounded-full bg-[var(--color-bg)]',
            iconBoxCls,
          )}
        >
          <Icon className={cn('text-[var(--color-text-sub)]', iconCls)} />
        </div>
      )}
      <p className="text-sm font-semibold text-[var(--color-text)]">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm px-4 text-xs text-[var(--color-text-sub)]">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
